import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatBadge } from '@angular/material/badge';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import {
  AgeQueueChipsComponent,
  ApiService,
  AuthorizationService,
  CustomIconService,
  ErrorService,
  HasAccessDirective,
  InsightQueueChipsComponent,
  MessageService,
  NavbarComponent,
  QueueChipsComponent,
  StalePriceService,
  StoreService,
  TitleService,
  TranslationLoaderService
} from '@ng-cloud/badger-core';
import { LayoutAlignDirective, LayoutDirective, ShowHideDirective } from '@ngbracket/ngx-layout';
import { Observable, switchMap } from 'rxjs';
import { RobotEscalationIdleAlertService } from './skybox/robot-escalation-idle-alert.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'fleet-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgeQueueChipsComponent,
    AsyncPipe,
    HasAccessDirective,
    InsightQueueChipsComponent,
    LayoutAlignDirective,
    LayoutDirective,
    MatBadge,
    MatButton,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTooltip,
    NavbarComponent,
    QueueChipsComponent,
    RouterLink,
    RouterOutlet,
    ShowHideDirective
  ]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  // Matches the roles that can see the Stores menu item.
  private static readonly STALE_INDICATOR_ROLES = ['admin', 'user', 'skybox_level_1'];

  private resizeObservable: ResizeObserver;
  private _navMenuButton: MatButton;
  @ViewChild('navMenuTrigger') navMenuTrigger: MatMenuTrigger;

  @ViewChild('navMenuButton') set navMenuButton(navMenuButton: MatButton) {
    this._navMenuButton = navMenuButton;
    if (this._navMenuButton) {
      this.resizeObservable.observe(
        this._navMenuButton._elementRef.nativeElement
      );
    }
  }

  title = 'Fleet';
  isInsightMod = false;
  defaultTheme = 'badger-dark';
  robotEscalationUnseenCount$: Observable<number>;

  // True when at least one store the user can access currently has stale price data.
  // Drives visibility of the stale-price shortcut button in the header.
  hasStalePriceStores = false;
  private canViewStaleIndicator = false;
  private staleIndicatorLoaded = false;
  private staleIndicatorLoading = false;

  constructor(
    private messageService: MessageService,
    private errorService: ErrorService,
    private customIconService: CustomIconService,
    public authService: AuthorizationService,
    private translationLoader: TranslationLoaderService,
    private robotEscalationIdleAlertService: RobotEscalationIdleAlertService,
    private storeService: StoreService,
    private stalePriceService: StalePriceService,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    TitleService.appTitle = this.title;
    ApiService.apis = environment.apis;

    this.customIconService.addCustomIcon(
      'badger-light',
      '../assets/badger_dark.svg'
    );
    this.customIconService.addCustomIcon(
      'badger-dark',
      '../assets/badger_dark.svg'
    );

    this.customIconService.addCustomIcon('inspect', '../assets/inspect.svg');
    this.customIconService.addCustomIcon('top_stock', '../assets/barcode.svg');
    this.customIconService.addCustomIcon('insight', '../assets/insight.svg');
    this.customIconService.addCustomIcon(
      'insight_marty',
      '../assets/insight_marty.svg'
    );
    this.customIconService.addCustomIcon(
      'inspect_marty',
      '../assets/inspect_marty.svg'
    );
    this.customIconService.addCustomIcon(
      'security_checkpoint',
      '../assets/security_checkpoint_bot.svg'
    );
    this.customIconService.addCustomIcon(
      'security_bot',
      '../assets/security_bot.svg'
    );
    this.customIconService.addCustomIcon('rfid_bot', '../assets/rfid_bot.svg');
    this.customIconService.addCustomIcon(
      'nav_zone_group',
      '../assets/nav_zone_group.svg'
    );
    this.customIconService.addCustomIcon(
      'series_start',
      '../assets/series_start_white.svg'
    );
    this.customIconService.addCustomIcon(
      'series_end',
      '../assets/series_end_white.svg'
    );

    this.customIconService.addCustomIcon(
      'price_mismatch_alt',
      '../assets/price_alt.svg'
    );
    this.customIconService.addCustomIcon(
      'sale_price_mismatch_alt',
      '../assets/sale_price_alt.svg'
    );
    this.customIconService.addCustomIcon(
      'top_stock_alt',
      '../assets/top_stock_alt.svg'
    );
    this.customIconService.addCustomIcon(
      'hole_oos_alt',
      '../assets/out_alt.svg'
    );
    this.customIconService.addCustomIcon(
      'out_of_stock_alt',
      '../assets/out_alt.svg'
    );
    this.customIconService.addCustomIcon('eOOS_alt', '../assets/wrong_alt.svg');
    this.customIconService.addCustomIcon(
      'viewport_alt',
      '../assets/viewport_alt.svg'
    );
    this.customIconService.addCustomIcon(
      'viewport_issue_alt',
      '../assets/viewport_alt.svg'
    );
    this.customIconService.addCustomIcon(
      'unconfirmed_product_alt',
      '../assets/unconfirmed_product_alt.svg'
    );

    this.customIconService.addCustomIcon(
      'price_mismatch',
      '../assets/price.svg'
    );
    this.customIconService.addCustomIcon(
      'sale_price_mismatch',
      '../assets/sale_price.svg'
    );
    this.customIconService.addCustomIcon('hole_oos', '../assets/out.svg');
    this.customIconService.addCustomIcon('out_of_stock', '../assets/out.svg');
    this.customIconService.addCustomIcon('eOOS', '../assets/wrong.svg');
    this.customIconService.addCustomIcon('viewport', '../assets/viewport.svg');
    this.customIconService.addCustomIcon(
      'viewport_issue',
      '../assets/viewport.svg'
    );
    this.customIconService.addCustomIcon(
      'unconfirmed_product',
      '../assets/unconfirmed_product.svg'
    );
    this.customIconService.addCustomIcon(
      'system_check',
      '../assets/insight_bot.svg'
    );

    this.customIconService.loadCustomIcons();

    this.translationLoader.initialize();
    this.robotEscalationUnseenCount$ =
      this.robotEscalationIdleAlertService.newIdleCount$;
  }

  ngOnInit() {
    this.robotEscalationIdleAlertService.initialize();
    this.errorService.exceptions().subscribe((exception) => {
      this.messageService.alert(exception.error, exception);
    });
    this.errorService.alerts().subscribe((alert) => {
      this.messageService.alert(alert);
    });

    this.authService.currentRoles().subscribe((roles) => {
      if (roles.includes('insight_moderator') && !roles.includes('admin')) {
        this.isInsightMod = true;
      }

      this.canViewStaleIndicator = roles.some((role) =>
        AppComponent.STALE_INDICATOR_ROLES.includes(role)
      );

      if (
        this.canViewStaleIndicator &&
        !this.staleIndicatorLoaded &&
        !this.staleIndicatorLoading
      ) {
        this.loadStaleIndicatorState();
      }

      this.changeDetectorRef.markForCheck();
    });
  }

  shouldShowStaleIndicator(): boolean {
    return this.canViewStaleIndicator && this.hasStalePriceStores;
  }

  navigateToStaleStores() {
    this.router.navigate(['/stores'], { queryParams: { staleOnly: true } });
  }

  /** Fetches the user's stores and checks whether any currently has stale price data. */
  private loadStaleIndicatorState() {
    this.staleIndicatorLoading = true;

    this.storeService.getStores().pipe(
      switchMap((stores) => this.stalePriceService.hasStaleStore(stores))
    ).subscribe({
      next: (hasStaleStores) => {
        this.hasStalePriceStores = hasStaleStores;
        this.finishStaleIndicatorLoad();
      },
      error: () => {
        this.finishStaleIndicatorLoad();
      }
    });
  }

  private finishStaleIndicatorLoad() {
    this.staleIndicatorLoaded = true;
    this.staleIndicatorLoading = false;
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.resizeObservable && this.resizeObservable.disconnect();
  }

  ngAfterViewInit(): void {
    this.createResizeObservable();
  }

  createResizeObservable() {
    this.resizeObservable = new ResizeObserver((entries) => {
      if (entries[0].contentRect.width === 0) {
        this.navMenuTrigger.closeMenu();
      }
    });
  }

  openRobotEscalationIdleDialog() {
    this.robotEscalationIdleAlertService.openDialog('manual');
  }
}
